import { ActionIcon, Box, Button, Code, Divider, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { IconRefresh, IconTrash } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { env } from "@/config/environment";
import { getSharedApiClient, useAsyncLoading } from "@/shared-utils";
import { userCache } from "@/shared-utils/auth/userCache";

const getFeatureOverrideClient = () =>
    getSharedApiClient().extend(() => ({
        prefixUrl: `${env.backendUrls.k8s.apiBaseUrl}/api/backoffice/feature-override`,
    }));

export const FeatureOverrideContainer = () => {
    const [featureKey, setFeatureKey] = useState("");
    const [value, setValue] = useState("");
    const [featureOverrides, setFeatureOverrides] = useState<string>("");

    const [isLoadingSet, setFeatureFlagOverride] = useAsyncLoading(async () => {
        await getFeatureOverrideClient().patch(featureKey, { json: { featureKey, value: JSON.parse(value) } });
        await fetchOverrides();
    });

    const [isLoadingRefresh, refreshOverrides] = useAsyncLoading(async () => {
        await fetchOverrides();
    });

    const [isLoadingDelete, deleteFeatureKey] = useAsyncLoading(async () => {
        if (!featureKey) return;
        await getFeatureOverrideClient().delete(featureKey);
        setFeatureKey("");
        setValue("");
        await fetchOverrides();
    });

    const fetchOverrides = async () => {
        const response = (await getFeatureOverrideClient().get("").json()) ?? {};
        setFeatureOverrides(JSON.stringify(response, null, 2));
    };

    useEffect(() => {
        void fetchOverrides();
    }, []);

    const existingKeys = useMemo(() => {
        try {
            const parsed = JSON.parse(featureOverrides || "{}");
            return Object.keys(parsed);
        } catch {
            return [];
        }
    }, [featureOverrides]);

    const keyExists = featureKey && existingKeys.includes(featureKey);
    if (userCache.role !== "Dev")
        return (
            <Stack>
                <Text>
                    You are not authorized to access this view. If you should have access, please contact the Workspace
                    team.
                </Text>
            </Stack>
        );

    return (
        <Stack>
            <Text>Employee ID: {userCache.employeeId}</Text>
            <Box pos="relative">
                <ActionIcon
                    variant="subtle"
                    onClick={refreshOverrides}
                    loading={isLoadingRefresh}
                    aria-label="Refresh feature overrides"
                    pos="absolute"
                    top={8}
                    right={8}
                    style={{ zIndex: 1 }}
                >
                    <IconRefresh size={16} />
                </ActionIcon>
                <Code block>{featureOverrides}</Code>
            </Box>
            <Divider />
            <Group align="flex-end">
                <TextInput
                    placeholder="Feature Flag Name"
                    value={featureKey}
                    onChange={(event) => setFeatureKey(event.currentTarget.value)}
                    required
                    style={{ flex: 1 }}
                />
                <Tooltip
                    label={keyExists ? "Delete this feature key" : "Feature key does not exist"}
                    zIndex="var(--mantine-priority-highest)"
                >
                    <ActionIcon
                        onClick={deleteFeatureKey}
                        loading={isLoadingDelete}
                        disabled={!keyExists}
                        variant="subtle"
                        color="red"
                        size="lg"
                        aria-label="Delete feature key"
                    >
                        <IconTrash size={16} />
                    </ActionIcon>
                </Tooltip>
            </Group>
            <Group align="flex-end">
                <TextInput
                    placeholder="Feature Flag Value"
                    value={value}
                    onChange={(event) => setValue(event.currentTarget.value)}
                    required
                    style={{ flex: 1 }}
                />
                <Button onClick={setFeatureFlagOverride} loading={isLoadingSet} disabled={!featureKey || !value}>
                    Set
                </Button>
            </Group>
        </Stack>
    );
};

export default FeatureOverrideContainer;
